import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, EditIcon, CheckIcon, TrashIcon, DownloadIcon } from 'lucide-react';
interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
}
interface Filter {
  key: string;
  label: string;
  options: string[];
}
interface DataTableProps {
  columns: Column[];
  data: any[];
  filters?: Filter[];
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onApprove?: (row: any) => void;
  onDelete?: (row: any) => void;
  searchPlaceholder?: string;
}
export function DataTable({
  columns,
  data,
  filters,
  onView,
  onEdit,
  onApprove,
  onDelete,
  searchPlaceholder = 'Search...'
}: DataTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const filteredData = data.filter(row => Object.values(row).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase())));
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
  return <div className="space-y-4" data-id="element-44">
      {/* Search and Filters */}
      <div className="flex items-center justify-between gap-4" data-id="element-45">
        <div className="flex items-center gap-4 flex-1" data-id="element-46">
          <div className="flex-1 relative max-w-md" data-id="element-47">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-48" />
            <input type="text" placeholder={searchPlaceholder} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-49" />
          </div>
          {filters && filters.map(filter => <select key={filter.key} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-50">
                <option value="" data-id="element-51">{filter.label}</option>
                {filter.options.map(option => <option key={option} value={option} data-id="element-52">
                    {option}
                  </option>)}
              </select>)}
        </div>
        <div className="flex items-center gap-2" data-id="element-53">
          <button onClick={() => alert('Exporting to Excel...')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-54">
            <DownloadIcon size={16} data-id="element-55" />
            Export Excel
          </button>
          <button onClick={() => alert('Exporting to PDF...')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-56">
            <DownloadIcon size={16} data-id="element-57" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-58">
        <div className="overflow-x-auto" data-id="element-59">
          <table className="w-full" data-id="element-60">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-61">
              <tr data-id="element-62">
                {columns.map(column => <th key={column.key} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-63">
                    {column.label}
                  </th>)}
                {(onView || onEdit || onApprove || onDelete) && <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-64">
                    Actions
                  </th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-65">
              {paginatedData.map((row, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-66">
                  {columns.map(column => <td key={column.key} className="px-6 py-4 text-sm text-gray-900" data-id="element-67">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>)}
                  {(onView || onEdit || onApprove || onDelete) && <td className="px-6 py-4 text-sm" data-id="element-68">
                      <div className="flex items-center gap-2" data-id="element-69">
                        {onView && <button onClick={() => onView(row)} className="p-1.5 text-gray-600 hover:text-[#1B5E20] hover:bg-gray-100 rounded transition-colors" title="View" data-id="element-70">
                            <EyeIcon size={16} data-id="element-71" />
                          </button>}
                        {onEdit && <button onClick={() => onEdit(row)} className="p-1.5 text-gray-600 hover:text-[#1B5E20] hover:bg-gray-100 rounded transition-colors" title="Edit" data-id="element-72">
                            <EditIcon size={16} data-id="element-73" />
                          </button>}
                        {onApprove && <button onClick={() => onApprove(row)} className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded transition-colors" title="Approve" data-id="element-74">
                            <CheckIcon size={16} data-id="element-75" />
                          </button>}
                        {onDelete && <button onClick={() => onDelete(row)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded transition-colors" title="Delete" data-id="element-76">
                            <TrashIcon size={16} data-id="element-77" />
                          </button>}
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-78">
          <div className="text-sm text-gray-700" data-id="element-79">
            Showing {startIndex + 1}-
            {Math.min(startIndex + itemsPerPage, filteredData.length)} of{' '}
            {filteredData.length}
          </div>
          <div className="flex items-center gap-2" data-id="element-80">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-81">
              <ChevronLeftIcon size={16} data-id="element-82" />
            </button>
            {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded-lg ${currentPage === page ? 'bg-[#1B5E20] text-white' : 'border border-gray-300 hover:bg-gray-50'}`} data-id="element-83">
                {page}
              </button>)}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-84">
              <ChevronRightIcon size={16} data-id="element-85" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}